"use client";

import React, { useEffect, useState } from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { buildImageLink } from "@/lib/image";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "next/image";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { CircleCheck, TriangleAlert } from "lucide-react";

interface CarouselProps {
  images: {
    path: string;
  }[];
  verified: boolean;
}

export default function ImageCarousel({ images, verified }: CarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);

    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", handleSelect);

    const intervalId = setInterval(() => {
      if (api.selectedScrollSnap() + 1 === api.scrollSnapList().length) {
        setCurrent(0);
        api.scrollTo(0);
      } else {
        api.scrollNext();
      }
    }, 5000);

    return () => {
      api.off("select", handleSelect);
      clearInterval(intervalId);
    };
  }, [api]);

  return (
    <TooltipProvider>
      <div className="relative flex flex-col items-center">
        <Carousel
          setApi={setApi}
          className="w-full overflow-hidden lg:max-w-none"
          opts={{
            align: "start",
            loop: true,
          }}
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                <AspectRatio ratio={16 / 9} className="overflow-hidden">
                  <Image
                    src={buildImageLink(image) ?? "/assets/cube.webp"}
                    alt={`Slide ${index + 1}`}
                    className="h-auto w-full overflow-hidden rounded-md border border-white/5 object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    fill
                    priority={index === 0}
                  />
                  <div className="absolute inset-0 z-10 w-full pl-1 pt-1">
                    {verified ? (
                      <Tooltip>
                        <TooltipTrigger className="flex justify-center">
                          <Badge
                            variant="accent"
                            className="bg-background/50 text-white backdrop-blur hover:bg-background/75"
                          >
                            <CircleCheck size={14} /> Verified
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          Verified script
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger className="flex justify-center">
                          <Badge
                            variant="destructive"
                            className="gap-1 bg-background/50 text-white backdrop-blur hover:bg-background/75"
                          >
                            <TriangleAlert size={14} /> Unverified
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          Unverified script
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </AspectRatio>
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 1 && (
            <>
              <CarouselPrevious />
              <CarouselNext />
            </>
          )}
        </Carousel>
        {count > 1 && (
          <div className="absolute bottom-4 flex gap-2 self-center lg:justify-center">
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                className={`h-2 rounded-full border border-white/5 opacity-75 transition-all duration-300 ${
                  current === i ? "w-4 bg-primary" : "w-2 bg-background"
                }`}
                onClick={() => {
                  api?.scrollTo(i);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
